import { GRAVITY, JUMP_GRAVITY_SCALE } from '../constants.js';
import { simulateTrajectory } from '../sim/physics.js';

export interface ShotOpts {
  gravityScale?: number;
  windScale?: number;
  minSpeed: number;
  maxSpeed: number;
}

export interface SolvedShot {
  vx: number;
  vy: number;
  speed: number;
  angle: number;
  /** Miss distance of the final refined shot (px). */
  miss: number;
}

/**
 * Analytic minimum-speed launch angle for a ballistic hit (no wind), world
 * coords (y down). Null when the target is out of range at maxSpeed.
 */
export function requiredVelocity(
  from: { x: number; y: number },
  to: { x: number; y: number },
  g: number,
  speedClamp: { min: number; max: number },
): { vx: number; vy: number; speed: number; angle: number } | null {
  const dx = to.x - from.x;
  const dyUp = from.y - to.y; // y-up displacement of target
  if (Math.abs(dx) < 1e-6 && Math.abs(dyUp) < 1e-6) return null;
  if (Math.abs(dx) < 1e-6) {
    // Straight up/down: v = sqrt(2 g h) for straight up
    const h = Math.max(0, dyUp);
    const speed = Math.min(speedClamp.max, Math.max(speedClamp.min, Math.sqrt(2 * g * h)));
    return { vx: 0, vy: -speed, speed, angle: Math.PI / 2 };
  }

  const x = Math.abs(dx);
  const dirX = Math.sign(dx);
  let best: { vx: number; vy: number; speed: number; angle: number } | null = null;

  // Sweep angles; required speed formula per angle; keep the feasible minimum.
  // Up to 89° so close-and-high targets get their steep solution instead of
  // falling through to the out-of-range fallback.
  for (let deg = 5; deg <= 89; deg += 2) {
    const th = (deg * Math.PI) / 180;
    const denom = 2 * Math.cos(th) * Math.cos(th) * (x * Math.tan(th) - dyUp);
    if (denom <= 1e-6) continue;
    const v2 = (g * x * x) / denom;
    if (v2 <= 0) continue;
    const speed = Math.sqrt(v2);
    if (speed > speedClamp.max) continue;
    if (!best || speed < best.speed) {
      const vx = dirX * speed * Math.cos(th);
      const vy = -speed * Math.sin(th);
      best = { vx, vy, speed, angle: th };
    }
  }
  if (!best) return null;
  // Too gentle for the weapon's minimum? Bump to min speed at that angle.
  if (best.speed < speedClamp.min) {
    const th = best.angle;
    const speed = speedClamp.min;
    best = {
      vx: dirX * speed * Math.cos(th),
      vy: -speed * Math.sin(th),
      speed,
      angle: th,
    };
  }
  return best;
}

function missOf(
  from: { x: number; y: number },
  vx: number,
  vy: number,
  to: { x: number; y: number },
  terrain: Uint8Array,
  wind: number,
  gravityScale: number,
  windScale: number,
): number {
  const r = simulateTrajectory(from.x, from.y, vx, vy, terrain, wind, {
    gravityScale,
    windScale,
    maxT: 8,
  });
  return Math.hypot(r.endX - to.x, r.endY - to.y);
}

/**
 * Terrain-aware shot solver: analytic seed, then hill-climbing refinement
 * against the real integrator (so wind and intervening terrain are respected).
 * Used by the AI, weapon previews, and volley re-aims; character jumps use
 * `solveJump` below.
 */
export function solveShot(
  from: { x: number; y: number },
  to: { x: number; y: number },
  terrain: Uint8Array,
  wind: number,
  opts: ShotOpts,
): SolvedShot | null {
  const g = GRAVITY * (opts.gravityScale ?? 1);
  const gravityScale = opts.gravityScale ?? 1;
  const windScale = opts.windScale ?? 1;
  const seed = requiredVelocity(from, to, g, { min: opts.minSpeed, max: opts.maxSpeed });
  if (!seed) {
    // Out of range: lob toward the target at max power (best effort).
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const v = opts.maxSpeed;
    const angle = Math.atan2(-dy, dx) * 0.55; // flatten toward a lob
    const vx = Math.cos(angle) * v * Math.sign(dx || 1);
    const vy = -Math.abs(Math.sin(angle)) * v;
    const miss = missOf(from, vx, vy, to, terrain, wind, gravityScale, windScale);
    return { vx, vy, speed: v, angle: Math.atan2(-vy, vx), miss };
  }
  return refine(from, to, terrain, wind, seed, { ...opts, gravityScale, windScale });
}

/**
 * Shared hill-climb: perturb the analytic seed's angle and speed, keep what
 * gets closer to the target under the real integrator (wind + terrain).
 */
function refine(
  from: { x: number; y: number },
  to: { x: number; y: number },
  terrain: Uint8Array,
  wind: number,
  seed: { vx: number; vy: number },
  opts: { minSpeed: number; maxSpeed: number; gravityScale: number; windScale: number },
): SolvedShot {
  let bx = seed.vx;
  let by = seed.vy;
  let bmiss = missOf(from, bx, by, to, terrain, wind, opts.gravityScale, opts.windScale);

  const speeds = Math.hypot(bx, by);
  let angle = Math.atan2(-by, bx);
  const steps = [8, 4, 2, 1, 0.5];
  for (const stepDeg of steps) {
    const step = (stepDeg * Math.PI) / 180;
    let improved = false;
    for (const da of [-1, 0, 1]) {
      for (const ds of [-1, 0, 1]) {
        if (da === 0 && ds === 0) continue;
        const a = angle + da * step;
        const v = speeds * (1 + ds * step * 1.6);
        const clamped = Math.min(opts.maxSpeed, Math.max(opts.minSpeed, v));
        const vx = Math.cos(a) * clamped;
        const vy = -Math.sin(a) * clamped;
        const miss = missOf(from, vx, vy, to, terrain, wind, opts.gravityScale, opts.windScale);
        if (miss < bmiss - 0.5) {
          bmiss = miss;
          bx = vx;
          by = vy;
          angle = a;
          improved = true;
        }
      }
    }
    if (!improved && stepDeg <= 1) break;
  }

  return { vx: bx, vy: by, speed: Math.hypot(bx, by), angle, miss: bmiss };
}

/**
 * Aimed character jump — the ONE path both the engine and the client preview
 * use, so the dotted arc you drag is the arc you fly.
 *
 * Reachable points get the min-speed ballistic solution refined against the
 * real integrator (light gravity + wind). Unreachable points — above the
 * apex, or past max range — get a cannon shot AT the point: max power, aimed
 * along the direct line but never flatter than 45° (a flat lob from ground
 * level just skims into the dirt) and never steeper than vertical.
 *
 * Drag-aimed jumps skip this entirely: they submit the raw launch vector
 * (`Intent.vx/vy`) and fly exactly what the preview drew.
 */
export function solveJump(
  from: { x: number; y: number },
  to: { x: number; y: number },
  terrain: Uint8Array,
  wind: number,
  maxSpeed: number,
): SolvedShot {
  const minSpeed = maxSpeed * 0.3;
  const g = GRAVITY * JUMP_GRAVITY_SCALE;
  const seed = requiredVelocity(from, to, g, { min: minSpeed, max: maxSpeed });
  if (!seed) {
    const dx = to.x - from.x;
    const dyUp = from.y - to.y;
    const dir = dx >= 0 ? 1 : -1;
    const aim = Math.atan2(Math.max(0, dyUp), Math.abs(dx)); // 0..90°
    const a = Math.min(Math.PI / 2, Math.max(Math.PI / 4, aim));
    const vx = Math.cos(a) * maxSpeed * dir;
    const vy = -Math.sin(a) * maxSpeed;
    const miss = missOf(from, vx, vy, to, terrain, wind, JUMP_GRAVITY_SCALE, 0.5);
    return { vx, vy, speed: maxSpeed, angle: a, miss };
  }
  return refine(from, to, terrain, wind, seed, {
    minSpeed,
    maxSpeed,
    gravityScale: JUMP_GRAVITY_SCALE,
    windScale: 0.5,
  });
}
